import { useState, useEffect } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import { AlertCircle, CheckCircle, ShoppingCart, Loader, TrendingDown } from 'lucide-react';

const CheckoutAnalysis = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [checkoutData, setCheckoutData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedSite) {
      fetchCheckoutData();
    } else {
      setLoading(false);
      setError('Please select a site');
    }
  }, [user, selectedSite]);

  const fetchCheckoutData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/app/api/analytics/checkout-analysis.php?siteId=${selectedSite.id}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setCheckoutData(result.data);
      } else {
        setError(result.message || 'Failed to fetch checkout data');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading checkout analysis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-red-50 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="h-8 w-8 text-blue-600" />
            Checkout & Payment Analysis
          </h1>
          <p className="text-gray-600 mt-2">Payment methods, checkout options, and cart abandonment metrics</p>
        </div>

        {/* Summary Stats */}
        {checkoutData.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600">Sites Analyzed</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{checkoutData.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600">Guest Checkout Enabled</p>
              <p className="text-3xl font-bold text-green-600 mt-2">
                {checkoutData.filter(d => d.guest_checkout_enabled && !d.error).length}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600">Total Abandoned Carts</p>
              <p className="text-3xl font-bold text-orange-600 mt-2">
                {checkoutData.reduce((sum, d) => sum + (d.abandoned_carts || 0), 0)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Last 7 days</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600">Lost Revenue</p>
              <p className="text-3xl font-bold text-red-600 mt-2">
                ${checkoutData.reduce((sum, d) => sum + (d.abandoned_cart_value || 0), 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Last 7 days</p>
            </div>
          </div>
        )}

        {/* Detailed Data */}
        <div className="space-y-6">
          {checkoutData.map((site) => (
            <div key={site.site_id} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ShoppingCart className="h-6 w-6 text-blue-600" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{site.site_name}</h3>
                    </div>
                  </div>
                  {site.error ? (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                </div>
              </div>

              <div className="p-6">
                {site.error ? (
                  <div className="rounded-lg bg-red-50 p-4 flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-red-900">Error</p>
                      <p className="text-sm text-red-700">{site.error}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Checkout Configuration */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <ShoppingCart className="h-4 w-4" />
                        Checkout Configuration
                      </h4>
                      <div className="flex items-center p-3 bg-gray-50 rounded gap-3">
                        {site.guest_checkout_enabled ? (
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        ) : (
                          <AlertCircle className="h-6 w-6 text-yellow-600" />
                        )}
                        <span className="text-sm text-gray-700">Guest Checkout</span>
                        <span className={`text-sm font-semibold ${site.guest_checkout_enabled ? 'text-green-600' : 'text-yellow-600'}`}>
                          {site.guest_checkout_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>

                    {/* Cart Abandonment */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <TrendingDown className="h-4 w-4" />
                        Cart Abandonment (Last 7 Days)
                      </h4>
                      {site.cart_api_available === false ? (
                        <div className="p-4 bg-yellow-50 rounded border border-yellow-200">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-yellow-900">Cart Data Unavailable</p>
                              <p className="text-xs text-yellow-700 mt-1">{site.cart_api_note || 'Cart API access is restricted'}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 bg-orange-50 rounded border border-orange-200">
                            <p className="text-xs text-orange-600 uppercase font-semibold">Abandoned Carts</p>
                            <p className="text-2xl font-bold text-orange-900 mt-1">
                              {site.abandoned_carts}
                            </p>
                            <p className="text-xs text-orange-700 mt-2">
                              Active carts without completed purchase
                            </p>
                          </div>
                          <div className="p-4 bg-red-50 rounded border border-red-200">
                            <p className="text-xs text-red-600 uppercase font-semibold">Lost Revenue</p>
                            <p className="text-2xl font-bold text-red-900 mt-1">
                              ${(site.abandoned_cart_value || 0).toFixed(2)}
                            </p>
                            <p className="text-xs text-red-700 mt-2">
                              Estimated value of abandoned orders
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Recommendations & Notes */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Checkout Insights & Recommendations</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>• <strong>Guest Checkout:</strong> Enable guest checkout to reduce friction and improve conversion rates</li>
            <li>• <strong>Abandoned Carts:</strong> Monitor abandoned carts and implement recovery email campaigns. If showing 0, either there are truly no abandoned carts, or the cart API access may be restricted in your Magento configuration.</li>
            <li>• <strong>Checkout Flow:</strong> Analyze checkout flow for potential drop-off points and streamline the process</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CheckoutAnalysis;
